import { createContext, ReactElement, ReactNode, useContext, useMemo } from 'react'
import { useNavigate } from 'react-router'

import { useDeleteSalesDocumentDraft } from '@features/invoices/hooks/useDeleteSalesDocumentDraft'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { getOrganizationViewUrl } from '../../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../../utils/routing/reactRoute'
import { useSaveAndActivateRecurringInvoice } from '../hooks/recurringInvoice/useSaveAndActivateRecurringInvoice'
import {
  SaveRecurringInvoiceDraftOptions,
  useSaveRecurringInvoiceDraft,
} from '../hooks/recurringInvoice/useSaveRecurringInvoiceDraft'
import { useInvoiceEditorDocumentType } from '../hooks/useInvoiceEditorDocumentType'
import { useInvoiceEditorFormSubmit } from '../hooks/useInvoiceEditorFormSubmit'
import { useSaveInvoiceAsDraft } from '../hooks/useSaveInvoiceAsDraft'
import { useUpdateApprovedQuote } from '../hooks/useUpdateApprovedQuote'
import { FinalizeActionDropdownItem } from '../types/finalizeActionDropdownItem'
import { InvoiceEditorDocumentType } from '../types/invoiceEditorDocumentType'

interface ContextState {
  deleteSalesDocumentDraft: (options: {
    approvedEstimateId?: string
    documentId: number
    documentType: InvoiceEditorDocumentType
  }) => void
  isDeletingSalesDocumentDraft: boolean
  isLoading: boolean
  isProcessing: boolean
  isSavingAndActivatingRecurringInvoice: boolean
  isSavingDraft: boolean
  isSavingQuote: boolean
  isSavingRecurringInvoiceDraft: boolean
  isSubmitting: boolean
  saveAndActivateRecurringInvoice: () => void
  saveAsDraft: () => void
  saveQuote: () => void
  saveRecurringInvoiceDraft: ({ onError, onSuccess }?: SaveRecurringInvoiceDraftOptions) => void
  submit: Record<FinalizeActionDropdownItem, Function>
}

const InvoiceEditorActionsContext = createContext<ContextState | undefined>(undefined)

interface InvoiceEditorActionsContextProviderProps {
  children?: ReactNode
}

export const InvoiceEditorActionsContextProvider = ({
  children,
}: InvoiceEditorActionsContextProviderProps): ReactElement => {
  const navigate = useNavigate()
  const { organization } = useUserOrganization()
  const { isQuote } = useInvoiceEditorDocumentType()
  const { isProcessing: isDeletingSalesDocumentDraft, deleteSalesDocumentDraft } = useDeleteSalesDocumentDraft({
    onSuccess: () => {
      const destination = isQuote
        ? reactRoute.organization.quotes.getMainRoute()
        : reactRoute.organization.invoices.getMainRoute()
      navigate(getOrganizationViewUrl(organization?.url, destination))
    },
  })
  const { isLoading: isLoadingSubmitActions, isSubmitting, submit } = useInvoiceEditorFormSubmit()
  const { isLoading: isLoadingSaveInvoiceAsDraft, isSaving: isSavingDraft, saveAsDraft } = useSaveInvoiceAsDraft()
  const {
    isLoading: isLoadingQuote,
    isProcessing: isSavingQuote,
    updateApprovedQuote: saveQuote,
  } = useUpdateApprovedQuote()

  const {
    saveRecurringInvoiceDraft,
    isLoading: isLoadingSaveRecurringInvoiceAsDraft,
    isSaving: isSavingRecurringInvoiceDraft,
  } = useSaveRecurringInvoiceDraft()

  const {
    saveAndActivateRecurringInvoice,
    isLoading: isLoadingSaveAndActivateRecurringInvoice,
    isProcessing: isSavingAndActivatingRecurringInvoice,
  } = useSaveAndActivateRecurringInvoice()

  const isLoading =
    isLoadingSubmitActions ||
    isLoadingSaveInvoiceAsDraft ||
    isLoadingQuote ||
    isLoadingSaveRecurringInvoiceAsDraft ||
    isLoadingSaveAndActivateRecurringInvoice

  const isProcessing =
    isSavingDraft ||
    isSavingQuote ||
    isSubmitting ||
    isSavingRecurringInvoiceDraft ||
    isSavingAndActivatingRecurringInvoice

  const contextValue = useMemo(
    () => ({
      deleteSalesDocumentDraft,
      isDeletingSalesDocumentDraft,
      isLoading,
      isProcessing,
      isSavingAndActivatingRecurringInvoice,
      isSavingDraft,
      isSavingQuote,
      isSavingRecurringInvoiceDraft,
      isSubmitting,
      saveAndActivateRecurringInvoice,
      saveAsDraft,
      saveQuote,
      saveRecurringInvoiceDraft,
      submit,
    }),
    [
      deleteSalesDocumentDraft,
      isDeletingSalesDocumentDraft,
      isLoading,
      isProcessing,
      isSavingAndActivatingRecurringInvoice,
      isSavingDraft,
      isSavingQuote,
      isSavingRecurringInvoiceDraft,
      isSubmitting,
      saveAndActivateRecurringInvoice,
      saveAsDraft,
      saveQuote,
      saveRecurringInvoiceDraft,
      submit,
    ],
  )

  return <InvoiceEditorActionsContext.Provider value={contextValue}>{children}</InvoiceEditorActionsContext.Provider>
}

export const useInvoiceEditorActions = () => {
  const context = useContext(InvoiceEditorActionsContext)

  if (!context) {
    throw new Error('InvoiceEditorActionsContext is missing in the module!')
  }

  return context
}
